import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
} from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { Document } from '../../../../generated/generated-types';
import { DocumentService } from '../../services/document.service';
import { IFormlyAddEditLineItemListComponent } from '../formly-add-edit-line-item-list/formly-add-edit-line-item-list.interface';
import { AllocatableLineItemService } from '../warehouse-disposition/services/allocatable-line-item.service';
import { WidgetWarehouseDispositionWorkspaceComponent } from '../warehouse-disposition/widget-warehouse-disposition-workspace/widget-warehouse-disposition-workspace.component';

@Component({
  selector: 'app-formly-document-warehouse-disposition',
  templateUrl: './formly-document-warehouse-disposition.component.html',
  host: {
    class: 'flex h-full min-h-0 flex-col overflow-hidden',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [WidgetWarehouseDispositionWorkspaceComponent],
})
export class FormlyDocumentWarehouseDispositionComponent extends FieldType<FieldTypeConfig> {
  private readonly documentService = inject(DocumentService);
  private readonly allocatableLineItemService = inject(AllocatableLineItemService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly documentId = computed(
    () =>
      this.formState?.detailComponent?.detailModelSig()?.id
      ?? (this.model as Document | undefined)?.id
      ?? undefined,
  );

  private lastSeenDocumentLineItemsChangedTick: number | undefined;

  private documentLineItemsRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    super();

    effect(() => {
      const embeddedDocumentId = this.documentId();
      const tick = this.documentService.documentLineItemsChangedTick();
      if (!embeddedDocumentId) {
        this.lastSeenDocumentLineItemsChangedTick = tick;
        return;
      }
      if (this.lastSeenDocumentLineItemsChangedTick === undefined) {
        this.lastSeenDocumentLineItemsChangedTick = tick;
        if (this.isDocumentAffectedByLastChange(embeddedDocumentId)) {
          this.scheduleRefreshFromDocumentLineItemsChanged();
        }
        return;
      }
      if (tick === this.lastSeenDocumentLineItemsChangedTick) {
        return;
      }
      const isAffected = this.isDocumentAffectedByLastChange(embeddedDocumentId);
      this.lastSeenDocumentLineItemsChangedTick = tick;
      if (!isAffected) {
        return;
      }
      this.scheduleRefreshFromDocumentLineItemsChanged();
    });

    this.destroyRef.onDestroy(() => {
      if (this.documentLineItemsRefreshTimer) {
        clearTimeout(this.documentLineItemsRefreshTimer);
        this.documentLineItemsRefreshTimer = null;
      }
    });
  }

  private isDocumentAffectedByLastChange(embeddedDocumentId: string): boolean {
    return this.documentService.getDocumentLineItemsChangedDocumentIds().includes(embeddedDocumentId);
  }

  private scheduleRefreshFromDocumentLineItemsChanged(): void {
    if (this.documentLineItemsRefreshTimer) {
      clearTimeout(this.documentLineItemsRefreshTimer);
    }
    this.documentLineItemsRefreshTimer = setTimeout(() => {
      this.documentLineItemsRefreshTimer = null;
      void this.refreshFromDocumentLineItemsChanged();
    }, 300);
  }

  async onDocumentAllocationChanged(): Promise<void> {
    const documentId = this.documentId();
    if (!documentId) {
      return;
    }
    await this.documentService.refetchOnlyDocumentLineItemCausedChanges(documentId);
    await this.refreshDocumentLineItemListFromAllocation();
    this.cdRef.markForCheck();
  }

  private async refreshFromDocumentLineItemsChanged(): Promise<void> {
    const documentId = this.documentId();
    if (!documentId) {
      return;
    }
    await this.allocatableLineItemService.refreshLineItems();
    await this.documentService.refetchOnlyDocumentLineItemCausedChanges(documentId);
    this.cdRef.markForCheck();
  }

  /** One-way sync: allocation commits can change line quantities on the document grid. */
  private async refreshDocumentLineItemListFromAllocation(): Promise<void> {
    const listFormlyWidget = this.options.formState?.dliListComponent as IFormlyAddEditLineItemListComponent | undefined;
    await listFormlyWidget?.addEditLineItemWidget()?.refresh();
  }
}
